<template>
    <div class="search-form" data-testid="search-block-form">
        <SearchField class="search-form__category">
            <SearchAutocompleteCategory v-model="category" :placeholder="categoryPlaceholder" />
        </SearchField>
        <SearchField class="search-form__location">
            <VJoyWrapper class="search-form__wrapper" no-margin justify="center">
                <MaltLocationAutocomplete
                    v-model="location"
                    class="search-form__location-autocomplete"
                    without-border
                    append-to-self
                    auto-option-focus
                    :label="getLocationPlaceholder"
                    :remote-allowed="true"
                    :remote-by-country="true"
                    prevent-option-selection
                    :remote-label="locationRemoteCountryLabel ?? ''"
                    :pt="pt"
                    :layer="[
                        {name: 'LOCALITY', size: 5},
                        {name: 'COUNTRY', size: 2},
                    ]"
                />
                <VJoyButton
                    class="search-form__submit"
                    variant="main"
                    :icon="submitIcon ?? 'search'"
                    icon-position="right"
                    :disabled="!hasQuery || navigatingToSearchPage"
                    :loading="navigatingToSearchPage"
                    data-testid="search-block-btn-submit"
                    @click="submit"
                >
                    {{ submitText }}
                </VJoyButton>
            </VJoyWrapper>
        </SearchField>
    </div>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {onKeyStroke} from '@vueuse/core';
    import {MaltLocationAutocomplete} from '#components';
    import {useTranslation} from '#imports';
    import type {Location} from '@malt/nuxt-common-components-module/types';
    import {VJoyButton, type TJoyIconsNames, VJoyWrapper} from '@maltjoy/core-vue';
    import SearchAutocompleteCategory from './SearchAutocompleteCategory.vue';
    import SearchField from './SearchField.vue';
    import useSearchHelpers from '../composables/useSearchHelpers';

    const props = defineProps<{
        categoryPlaceholder: string | null;
        locationPlaceholder: string | null;
        locationRemoteCountryLabel?: string | null;
        submitText: string | null;
        submitIcon?: TJoyIconsNames;
        submitLink: string | null;
        briefOrigin?: string;
        customizeSearchParams?: (queryParams: URLSearchParams) => URLSearchParams;
        isBrowsingGlobalMatching?: boolean;
    }>();

    const {getSearchURL} = useSearchHelpers();
    const {t} = useTranslation();

    const getLocationPlaceholder = computed(() => {
        return props.isBrowsingGlobalMatching ? t('search.location.com.label') : (props.locationPlaceholder ?? '');
    });

    const navigatingToSearchPage = ref(false);

    const category = defineModel<string>('keywords', {
        default: '',
    });

    const location = defineModel<Location>('location', {
        default: () => ({
            formattedAddress: '',
        }),
    });

    const pt = computed(() => ({
        option: {
            style: {
                fontSize: 'var(--joy-font-size-primary-400) !important',
                padding: 'var(--joy-core-spacing-2) var(--joy-core-spacing-5)',
            },
        },
        overlay: {
            style: {
                borderRadius: 'var(--joy-core-radius-3)',
                boxShadow: 'var(--joy-core-elevation-2)',
                border: 'none !important',
            },
        },
    }));

    const hasQuery = computed(() => category.value?.length >= 3);

    onKeyStroke('Enter', (e) => {
        const locationOverlayOpen = document.querySelector('.p-autocomplete-overlay');
        const categoryOverlayOpen = document.querySelector('.search-autocomplete__suggestions');
        if (hasQuery.value && !locationOverlayOpen && !categoryOverlayOpen) {
            submit();
        }
    });

    function submit() {
        navigatingToSearchPage.value = true;
        window.location.href = getSearchURL({
            baseURLLink: props.submitLink || '/s',
            query: category.value,
            location: location.value || null,
            sourceComponent: props.briefOrigin ?? undefined,
            customizeSearchParams: props.customizeSearchParams,
        });
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    :deep(.p-autocomplete-overlay) {
        margin-top: 20px !important;
    }

    .search-form {
        position: relative;
        border: 2px solid var(--joy-color-neutral-30);
        border-radius: var(--joy-core-radius-4);
        margin-bottom: calc(var(--joy-form-field-height-medium) + var(--joy-core-spacing-4));

        &__location-autocomplete {
            flex: 1;

            :deep(.search-input__clear-btn) {
                right: calc(var(--joy-core-spacing-1) * -1);
                .joy-icon {
                    color: var(--joy-color-neutral-40);
                }
            }
        }

        &__location {
            padding-left: 0;
        }

        &__wrapper {
            width: 100%;
        }

        &:after {
            content: '';
            display: block;
            background-color: var(--joy-color-neutral-30);
            position: absolute;
            top: var(--joy-core-spacing-12);
            left: var(--joy-core-spacing-3);
            height: 1px;
            width: calc(100% - 2 * var(--joy-core-spacing-3));
        }

        &:has(input:focus):after {
            opacity: 0;
        }

        .joy-icon {
            pointer-events: none;
        }

        .search-autocomplete {
            flex: 1;
            align-self: stretch;
            height: var(--joy-core-spacing-12);

            .search-input__clear-btn {
                top: 50%;
                transform: translateY(-50%);
                color: var(--joy-color-text-default);
                opacity: 0.5;

                &:before {
                    content: '';
                    width: 26px;
                    height: 26px;
                    border-radius: var(--joy-core-radius-4);
                    border: 1px dotted var(--joy-color-neutral-40);
                }
            }
        }

        &__submit.joy-button {
            position: absolute;
            top: calc(100% + 2px); // add form border width
            left: -2px; // aligned with form border
            width: calc(100% + 4px); // add form border width
            margin: var(--joy-core-spacing-4) 0;
        }

        /* RESPONSIVE */
        @include mq.screen_sm(min) {
            height: 100%;
            display: flex;
            align-items: stretch;
            margin-bottom: 0;
            border-radius: var(--joy-core-radius-5);

            &:after {
                height: 70%;
                width: 1px;
                top: 15%;
                left: 40%;
            }
            &__category {
                width: 40%;
                flex: none;
            }
            &__submit.joy-button {
                position: static;
                width: auto;
                min-height: var(--joy-form-field-height-small);
                font-size: var(--joy-font-size-primary-300);
                margin: var(--joy-core-spacing-3) 0;
            }
        }

        @include mq.screen_md(min) {
            &:after {
                left: 45%;
            }
            &__category {
                width: 45%;
            }
        }
    }
</style>
